import Vue from 'vue';
import { mapState, mapGetters } from 'vuex';
import _ from 'lodash';
import moment from 'moment-timezone';
import { diff } from 'deep-object-diff';
import { guid } from '@/mixins/guid';
import { PAGE_TYPES, QUEUE_TYPES } from '@/constants';
import { viewports } from '@/mixins/viewport-switcher';
import { queuedWork } from '@/mixins/queued-work';
import tooltip from '@/mixins/components/tooltip';
import DemoModal from '@/live-demo/modal';
import SearchContent from './search-content';
import AddPageTpl from './add-new';
import PageSettings from './page-settings';
import CreatorOptions from './creator-options';
import GlobalOptions from './global-options';
import DynamicData from './dynamic-data';
import EditorHistory from './history';
import PreviewOptions from './preview-options';
import SaveOptions from './save-options';
import CheckUnsaved from './check-unsaved';

export default Vue.component('top-control-bar', {
  template: require('./template.html'),
  mixins: [
    guid,
    viewports,
    queuedWork,
  ],
  computed: {
    ...mapGetters([
      'currentPage',
      'currentUser',
      'history',
      'showHistory',
      'initialCurrentPage',
      'currentSite',
      'editorLoader',
      'online',
      'globalOpenModal',
      'globalBounceDroplet',
      'globalToolBarOpen',
      'globalLoading',
      'previewOptions',
      'saveOptions',
      'viewport',
    ]),
    ...mapState({
      id: state => state.TopControlBar.currentPage._id,
      name: state => state.TopControlBar.currentPage.name,
    }),
    type() {
      return this.currentPage.type
        ? this.cleanType(this.currentPage.type.split('.')[0] || this.currentPage.type)
        : null;
    },
    pageType() {
      const type = this.currentPage.type;
      const text = PAGE_TYPES.includes(type) ? type : 'Droplet';
      return _.startCase(text);
    },
    getStatus() {
      const hasStatus = ['template', 'page'].includes(this.type);
      return hasStatus ? (` (${_.startCase(this.currentPage.status || 'published')})`) : '';
    },
    isDroplet() {
      return this.type ? this.type.match(/droplet|puddle|section/gi) : false;
    },
    previewEnabled() {
      return ['page', 'template'].includes(this.currentPage.type);
    },
    historyItems() {
      return this.history.map((item) => {
        let itemEdited = '';
        if (item.meta && item.meta.dropletType) {
          itemEdited = this._dropletType({ type: item.meta.dropletType }).name;
        }
        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const time = moment(item.timestamp).tz(timeZone).format('M/D - h:mm:ss a');
        const message = this.getMessage({ editType: item.type, itemEdited });
        const listItem = {
          _id: item._id,
          type: item.type,
          tip: `${message} - ${time}`,
          itemEdited,
          snapshot: item.snapshot,
          counts: item.counts,
          message,
          meta: item.meta,
          time,
        };
        return listItem;
      });
    },
  },
  props: ['is-full-page'],
  watch: {
    currentPage() {
      const { type } = this.currentPage;
      if (PAGE_TYPES.includes(type)) {
        this.$store.dispatch('setActiveDroplet', { droplet: {}, items: [] });
      }
    },
    globalOpenModal(open) {
      if (open) {
        this.$store.dispatch('setActiveDroplet', { droplet: {}, items: [] });
      }
    },
    viewport() {
      this._trackEvent({
        type: 'viewport_switch',
        meta: {
          viewport: this.viewport,
          site_name: this.currentSite.alias,
          site_type: this.currentSite.type,
          site_tier: this.currentSite.tier,
          page_name: this.currentPage.name,
          page_type: this.currentPage.type,
          page_url: this.currentPage.url,
        },
      });
    },
    history() {
      this.effect = 'drzTopControlBar-historyBtn-effect';
      setTimeout(() => {
        this.effect = false;
      }, 200);
    },
  },
  data() {
    return {
      s3Url: process.env.S3_ASSETS_URL,
      autoSave: null,
      screenSize: 0,
      autoSaveLoader: false,
      windowResizer: null,
      resizeTimer: null,
      autoSaveSuccess: false,
      effect: null,
      modals: {
        settings: false,
        dynamic: false,
        global: false,
        addPage: false,
        search: false,
        unsaved: false,
        creator: false,
      },
      names: {
        sectionBlock: 'Section',
      },
    };
  },
  components: {
    SearchContent,
    AddPageTpl,
    PageSettings,
    CreatorOptions,
    GlobalOptions,
    DynamicData,
    EditorHistory,
    DemoModal,
    PreviewOptions,
    SaveOptions,
    CheckUnsaved,
    tooltip,
  },
  mounted() {
    this.setAutoSave();
    this.screenSize = window.outerWidth;
    if (!this.windowResizer) {
      this.windowResizer = window.addEventListener('resize', () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => {
          this.screenSize = window.outerWidth;
        }, 350);
      });
    }
  },
  beforeDestroy() {
    clearInterval(this.autoSave);
  },
  methods: {
    TCBclick() {
      const overlay = document.querySelectorAll(
        '#drzBubble-overlay-container > .drpBubble-overlay',
      )[0];
      if (overlay) {
        overlay.click();
      }
    },
    cleanType(t) {
      return t === 'sectionBlock' ? 'Section' : t;
    },
    togglePreviewOptions() {
      this.$store.dispatch('setPreviewOptions', !this.previewOptions);
      this.$store.dispatch('setSaveOptions', false);
      this.$store.dispatch('toggleHistory', false);
    },
    toggleSaveOptions() {
      this.$store.dispatch('setSaveOptions', !this.saveOptions);
      this.$store.dispatch('toggleHistory', false);
      this.$store.dispatch('setPreviewOptions', false);
    },
    toggleHistory() {
      this.$store.dispatch('toggleHistory', !this.showHistory);
      this.$store.dispatch('setPreviewOptions', false);
      this.$store.dispatch('setSaveOptions', false);
    },
    toggleModal(modal) {
      this.modals[modal] = !this.modals[modal];
    },
    checkUnsaved() {
      if (this.$demo) {
        return;
      }
      const cP = this._deepOmit(this.currentPage, this._pageDiffIgnore);
      const iP = this._deepOmit(this.initialCurrentPage, this._pageDiffIgnore);
      if (_.isEqual(cP, iP)) {
        this.modals.unsaved = false;
        this.$router.push({ path: '/websites' });
      } else {
        // unsaved changes so warn user
        this.toggleModal('unsaved');
        this.$log.debug('Unsaved Page Difference: ', diff(cP, iP));
      }
    },
    getMessage(params) {
      const name = this.names[params.itemEdited] || params.itemEdited;
      const item = _.startCase(name);
      const messages = {
        droplet_settings: `${item} Setting Change`,
        pageload: 'Initial Page Load',
        droplet_styles: `${item} Style Change`,
        move_droplet: `Move ${item} Droplet`,
        new_droplet: `New ${item} Droplet`,
        remove_droplet: `Remove ${item} Droplet`,
        copy_droplet: `Copy ${item} Droplet`,
        resize_section: 'Resize Section',
        add_section: 'Add Section',
        load_recycled: `Load Recycled ${item}`,
        inline_text: `${item} Inline Text`,
        revert_page: 'Page Reverted',
      };
      return messages[params.editType];
    },
    async setAutoSave() {
      const autoSetting = this.currentSite.autosave;
      if (typeof autoSetting === 'undefined' || autoSetting) {
        this.autoSave = setInterval(async () => {
          if (
            !this.globalOpenModal &&
            !this.globalBounceDroplet &&
            !this.globalLoading.websites &&
            this.currentPage._id) {
            const cP = this._deepOmit(this.currentPage, this._pageDiffIgnore);
            const iP = this._deepOmit(this.initialCurrentPage, this._pageDiffIgnore);
            if (!_.isEqual(cP, iP)) {
              try {
                this.autoSaveLoader = true;
                await this.runPageSave();
                this.$log.debug('Run auto save');
              } catch (e) {
                this.$log.error(e.message || e);
              } finally {
                this.autoSaveLoader = false;
                this.autoSaveSuccess = 'Saved!';
                setTimeout(() => {
                  this.autoSaveSuccess = false;
                }, 2000);
              }
            }
          }
        }, 15000);
      }
    },
    async runPageSave() {
      const { type, status } = this.currentPage;
      const isDynamic = type === 'dynamic page';
      const isPage = PAGE_TYPES.includes(type);
      // since dynamic pages are elsewhere, need to condition them
      if (isDynamic) {
        await this._SEND([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'dynamic',
            value: this.currentSite.dynamic,
          },
        }]);
      }
      if (!isPage) {
        // droplet types
        await this._SEND([{
          name: 'updateSiteAttr',
          payload: {
            siteId: this.currentSite._id,
            attr: 'recycled',
            value: this.currentSite.recycled,
          },
        }]);
      }
      if (isPage && !isDynamic) {
        // normal page types
        const { _id, forms } = this.currentSite;
        await this.$lightning([{
          name: 'savePageItem',
          payload: {
            item: this.currentPage,
            site: { _id, forms },
          },
        }]);
        if (QUEUE_TYPES.includes(type) && status !== 'unpublished') {
          this.addToQueuedWork({ page: this.currentPage });
        }
        if (type === 'template' && !this._published) {
          // when a site is not yet live and published we want any template updates
          // to also update pages using it so they can continue building out their
          // site as they wish before selecting a plan. As publishing a template
          // is not usually allowed when a site is unpublished.
          await this.$store.dispatch('updateTemplatedPages', {
            template: this.currentPage,
          });
        }
      }
      this.$store.dispatch('updateInitialPage');
    },
    siteStatus(tooltip) {
      let status = '';
      if (!this.$demo) {
        const needsHtml = tooltip ? ' (live)' : ' <span class="drzGreen">(live)</span>';
        status = this._published ? needsHtml : ' (unpublished)';
      }
      return status;
    },
  },
});



// WEBPACK FOOTER //
// ./src/global-view/editor/page/top-control-bar/index.js